import { useState } from 'react';
import { FeatureFlags, useFeatureFlag } from '@/lib/tracking';
import { flowOverrideService } from '../services/flowOverrideService';

interface AppRedirectDecision {
  // True while the experiment value is still unknown — hold navigation until
  // it flips, otherwise users briefly land on the wrong flow.
  isResolving: boolean;
  shouldRedirectToApp: boolean;
}

/**
 * Decides whether the user is pushed to the mobile app right after signing in
 * (A/B experiment) or continues in the web chat.
 *
 * A `?flow=` param on the landing URL wins over the experiment for the whole
 * session: credit marketing campaigns link with `flow=web` to keep their users
 * on the web. Overridden users are never evaluated against the flag, so
 * they stay out of the experiment results instead of padding the control group.
 */
export const useAppRedirectAfterSignup = (): AppRedirectDecision => {
  const [override] = useState(() => flowOverrideService.get());
  const { isResolving, isEnabled } = useFeatureFlag(
    FeatureFlags.appRedirectAfterSignup,
    { enabled: !override }
  );

  if (override) {
    return { isResolving: false, shouldRedirectToApp: override === 'app' };
  }

  return { isResolving, shouldRedirectToApp: isEnabled };
};
